//@flow

import _ from 'lodash';
import ko from 'rgs-knockout';
import { EventEmitter } from 'event-emitter';

import defaultTemplate from './template/index.html';

import type { ObservableArray } from 'rgs-knockout';
import type { RgsHtmlTemplate } from 'rgs-knockout/types';
import type { Observable } from '../../../rgs-knockout';

export type TabEntity = {
	htmlContent: HTMLElement,
	isActive?: boolean,
	key?: any, //unique ID for every tab and it's <a href=#KEY> tag, see downwards
	menuKey?: string,
};

const ON_HANDLE_HASH_HREF = key => `#tabs/${key}`; // tip: <a href=`tabs/${key}`></a>

/*
	Numeration begins at 1 (tab) (but not 0!, so need)
	There are two ways to set activity of the tab :
	1) Pass 'activeTabIndex' as property into CONSTRUCTOR, else by default will 1;
	2) Assign 'isActive' property (see 'register' method') ELSE BY DEFAULT WIll 1 tab;
 */
export default class TabPanel extends EventEmitter {
	template: HTMLElement;
	tabs: ObservableArray<TabEntity>;
	currentActiveTab: Observable<number>;
	props: {
		onGenerateHref (...args: Array<any>): string;
	};

	constructor (params: {}) {
		super(...arguments);
		const { template, activeTabIndex, items, ...props } = _.defaults({ ...params }, {
			onGenerateHref: ON_HANDLE_HASH_HREF,
			template: defaultTemplate,
			activeTabIndex: 1,
			items: [],
		});

		this.tabs = ko.observableArray(items);
		this.currentActiveTab = ko.observable(activeTabIndex).extend({ notify: 'always' });
		this.isActiveTabTitleVisible = ko.observable();

		this.currentActiveTab.subscribe(v => {
			const activeTab = this.tabs()[v - 1];
			this.isActiveTabTitleVisible(activeTab && ko.unwrap(activeTab.visible));
		});

		this.template = template;
		this.props = props;
	}

	get _activeIndex () {
		return this.currentActiveTab.peek();
	}

	setActiveTabIndex (tabIndex: number) {
		if (this._activeIndex === tabIndex + 1) {
			return;
		}

		this.currentActiveTab(tabIndex + 1);
	}

	setActiveTab (tabKey: string) {
		const tabs = this.tabs();
		const tabIndex = tabs.findIndex(tab => tab.key === tabKey);
		if (tabIndex < 0) {
			return;
		}

		this.setActiveTabIndex(tabIndex);
	}

	isTabActive = (tabIndex: number): * => {
		return (
			this.currentActiveTab() === (tabIndex + 1)
		);
	};

	register (params: TabEntity): void {
		const {
			htmlContent,
			isActive,
			visible = ko.observable(true),
			...props
		} = params || {};

		if (isActive) {
			const belongIndex = this.tabs.peek().length + 1;
			this.currentActiveTab(belongIndex);
		}

		this.tabs.push({
			htmlContent, //it may be even observable, knockout supports it) +forbids malicious <script> tags
			visible,
			...props,
		});

		this.currentActiveTab.valueHasMutated();
	}

	getTemplate () {
		return {
			template: this.template,
			model: this,
		};
	}

	getKoTemplate (): RgsHtmlTemplate<TabPanel> {
		return {
			html: this.template(),
			data: this,
		};
	}

	_handleClickOnTab = (tabIndex: number, e: Event): boolean | void => {
		if (++tabIndex < 1) {
			return false;
		}

		if (this.currentActiveTab() === tabIndex) {
			return false;
		}

		this.currentActiveTab(tabIndex);
		this.emit('tab-changed', { tabIndex, event: e });

		return true;
	};
}
